import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpClient from "../network/http";
import { fetchCsrfToken } from "../context/AuthContext";
import BillingService from "../service/billing";
import {
  normalizeBootstrapPayload,
  normalizeItem,
} from "../util/billing/normalizeBillingEntities";

const baseURL = process.env.REACT_APP_BASE_URL;

export default function useBilling({ schoolid, schoolYearId }) {
  const queryClient = useQueryClient();
  const httpClient = new HttpClient(baseURL, () => fetchCsrfToken());
  const billingService = new BillingService(httpClient);

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["billingBootstrap", schoolid, schoolYearId],
    });

  const bootstrapQuery = useQuery(
    ["billingBootstrap", schoolid, schoolYearId],
    () =>
      billingService.getBootstrap({
        schoolid,
        school_year_id: schoolYearId,
      }),
    {
      enabled:
        !!schoolid &&
        schoolYearId !== null &&
        schoolYearId !== undefined &&
        String(schoolYearId) !== "",
      staleTime: 1000 * 60,
      select: normalizeBootstrapPayload,
    },
  );

  const upsertOneMutation = useMutation(
    (payload) => billingService.upsertOneRecord(payload),
    { onSuccess: invalidate },
  );

  const upsertBulkMutation = useMutation(
    (payload) => billingService.upsertBulkRecords(payload),
    { onSuccess: invalidate },
  );

  const createSessionMutation = useMutation(
    (body) => billingService.createSession(body),
    { onSuccess: invalidate },
  );

  const patchSessionMutation = useMutation(
    ({ id, body }) => billingService.patchSession(id, body),
    { onSuccess: invalidate },
  );

  const deleteSessionMutation = useMutation(
    (id) => billingService.deleteSession(id),
    { onSuccess: invalidate },
  );

  const reorderSessionsMutation = useMutation(
    (body) => billingService.reorderSessions(body),
    { onSuccess: invalidate },
  );

  const createSessionItemMutation = useMutation(
    ({ sessionId, body }) =>
      billingService.createSessionItem(sessionId, body),
    { onSuccess: invalidate },
  );

  const syncSessionItemsMutation = useMutation(
    ({ sessionId, items }) =>
      billingService.syncSessionItems(sessionId, items),
    { onSuccess: invalidate },
  );

  const patchSessionItemMutation = useMutation(
    ({ itemId, body }) => billingService.patchItem(itemId, body),
    { onSuccess: invalidate },
  );

  const deleteSessionItemMutation = useMutation(
    (itemId) => billingService.deleteItem(itemId),
    { onSuccess: invalidate },
  );

  const reorderSessionItemsMutation = useMutation(
    (body) => billingService.reorderItems(body),
    { onSuccess: invalidate },
  );

  const fetchSessionItems = async (sessionId) => {
    const raw = await billingService.getSessionItems(sessionId);
    const arr = Array.isArray(raw) ? raw : raw?.items ?? [];
    return arr.map((row) =>
      normalizeItem({
        ...row,
        billing_session_id: row.billing_session_id ?? sessionId,
      }),
    );
  };

  return {
    bootstrapQuery,
    upsertOneMutation,
    upsertBulkMutation,
    createSessionMutation,
    patchSessionMutation,
    deleteSessionMutation,
    reorderSessionsMutation,
    createSessionItemMutation,
    syncSessionItemsMutation,
    patchSessionItemMutation,
    deleteSessionItemMutation,
    reorderSessionItemsMutation,
    fetchSessionItems,
  };
}
